import type {
	ExtractInjectionAPI,
	NextEditorPlugin,
	OptionalPlugin,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id/local-id-plugin-type';
import type { SelectionPlugin } from '@atlaskit/editor-plugin-selection/selection-plugin-type';
import type { SessionAbortReason } from '@atlassian/editor-ai-common/analytics/normalized-telemetry-event';
import type { AIPlugin } from '@atlassian/editor-plugin-ai/aiPluginType';
import type { FAILURE_REASON } from '@atlassian/editor-plugin-ai/errors';
import type { AgentAttribution, AgentInfo } from '@atlassian/editor-plugin-ai/types';

/**
 * Optional context describing why a session is being aborted. Supplied by the
 * caller that knows the cause (the Rovo bridge) and used to classify the
 * session-outcome telemetry event.
 */
export type AbortSessionOptions = {
	/** Redacted error message, only set when `reason` is `aborted_due_to_error`. */
	errorMessage?: string;
	reason?: SessionAbortReason;
};

export type API = ExtractInjectionAPI<AiStreamingOrchestratorPlugin>;

export type StreamingCommandFactory<T extends StreamingCommand> = (
	api: API,
) => (args: T) => boolean;

export type MultiFormatContentType = 'text/markdown' | 'text/html' | 'application/adf+json';

type StreamableCommandMetadata = {
	contentType?: MultiFormatContentType;
};

// Source https://bitbucket.org/atlassian/conversational-ai-platform/src/main/modules/product/rovo/rovo-impl/src/main/resources/yaml/actions/internal/aifc_confluence_page_update.yaml#lines-67
// replaceDoc replaces the entire document; there is no localId because the
// top-level doc node has no localId and there is only one.
// contentType must be 'text/html' or 'application/adf+json' — the parser
// rejects any other value.
export type ReplaceDocCommand = StreamableCommandMetadata & {
	noStream?: boolean;
	type: 'replaceDoc';
};
export type ReplaceNodeCommand = StreamableCommandMetadata & {
	localId: string;
	noStream?: boolean;
	type: 'replaceNode';
};
export type InsertNodeBeforeCommand = StreamableCommandMetadata & {
	localId: string;
	noStream?: boolean;
	type: 'insertNodeBefore';
};
export type InsertNodeAfterCommand = StreamableCommandMetadata & {
	localId: string;
	noStream?: boolean;
	type: 'insertNodeAfter';
};
export type AppendNodeCommand = StreamableCommandMetadata &
	(
		| {
				// streamingType is used to show the highlight or not while streaming. In the create flow we don't want to show the highlight
				// as it is distracting when updating large documents
				noStream?: boolean;
				streamingType?: 'create' | 'update';
				type: 'appendNode';
		  }
		| {
				// streamingType is used to show the highlight or not while streaming. In the create flow we don't want to show the highlight
				// as it is distracting when updating large documents
				noStream?: boolean;
				streamingType?: 'create' | 'update';
				type: 'appendNodeToEnd';
		  }
	);

export type RemoveNodeCommand = { localId: string; type: 'removeNode' };
export type RemoveNodesCommand = { localIds: string[]; type: 'removeNodes' };
export type MoveNodeCommand = {
	anchorLocalId: string;
	localId: string;
	relationship: 'before' | 'after';
	type: 'moveNode';
};
export type SetAttrsCommand = {
	attrs: Record<string, unknown>;
	localId: string;
	type: 'setAttrs';
};

export type WrapNodeCommand =
	| {
			localId: string;
			type: 'wrap';
			wrapperAttrs: Record<string, unknown>;
			wrapperType: string;
	  }
	| {
			localId: string;
			type: 'wrapNode';
			wrapperAttrs: Record<string, unknown>;
			wrapperType: string;
	  };

export type StreamableCommandWithValue =
	| ReplaceDocCommand
	| ReplaceNodeCommand
	| InsertNodeBeforeCommand
	| InsertNodeAfterCommand
	| AppendNodeCommand;

// #region Public APIs
export type StreamableCommand = Exclude<StreamableCommandWithValue, 'value'>;

export type NonStreamableCommand =
	| RemoveNodeCommand
	| RemoveNodesCommand
	| MoveNodeCommand
	| WrapNodeCommand
	| SetAttrsCommand;

export type StreamingCommand = StreamableCommand | NonStreamableCommand;

/** One-shot document write. Typewriter uses `'stream'`. */
export type StreamingApplyMode = 'stream' | 'commit';

export const allStreamingCommands = [
	'replaceDoc',
	'replaceNode',
	'removeNode',
	'removeNodes',
	'moveNode',
	'insertNodeBefore',
	'insertNodeAfter',
	'appendNodeToEnd',
	'setAttrs',
	'wrap',
	'wrapNode',
	'appendNode',
] as const;

export type StreamingError = {
	apiName?: string;
	errorContent?: string;
	errorKey?: string;
	failureReason: FAILURE_REASON;
	statusCode?: number;
};

export type AiStreamingOrchestratorPluginOptions = {
	streamFlowPacerEnabled?: boolean;
	/**
	 * Set to `true` to enable `sessionOutcome` and `toolCallResult` analytics
	 * events emitted by the streaming orchestrator.
	 *
	 * Defaults to `false` (suppressed). Only enable in contexts where these
	 * events are meaningful and desired (e.g. the Confluence full-page editor).
	 */
	telemetryEventsEnabled?: boolean;
};

export type AiStreamingOrchestratorPlugin = NextEditorPlugin<
	'aiStreamingOrchestrator',
	{
		actions: {
			/**
			 * Abort the streaming immediately and end the experience.
			 * Rather than finishing the current streaming, this will discard any in-progress streaming
			 * and end the experience. No further streaming can be done until a new action is initialized.
			 *
			 * `options` carries why the abort happened so session-outcome telemetry
			 * can distinguish a user cancel from a timeout or an error.
			 */
			abortSession: (options?: AbortSessionOptions) => void;
			/**
			 * Write a finished tool call in one shot: apply (or resume at a
			 * stored range), emit the value, and finish. Does not start the
			 * typewriter pacer or wait on streaming decorations.
			 */
			commitToolCall: (
				command: StreamingCommand,
				value: string,
				options?: {
					abortedToolCallId?: string;
					hideLoadingScreen?: boolean;
					resume?: { toolCallId: string };
				},
			) => string;
			endSession: (error?: StreamingError) => Promise<void>;
			/**
			 * Finish (end) an active streaming action.
			 *
			 * @param actionId The actionId returned by a previous `initAction` call.
			 * @param error    Optional streaming error to surface.
			 * @param options.abortedToolCallId
			 *   When set, this finish is a queue-timeout force-finish. The orchestrator
			 *   captures the tool call's working positions (keyed by this id) before
			 *   ending the experience, so it can be resumed at its mapped position once
			 *   streaming ends (see `resumeAction`).
			 */
			finishAction: (
				actionId: string,
				error?: StreamingError,
				options?: { abortedToolCallId?: string },
			) => void;
			initAction: (
				command: StreamingCommand,
				options?: {
					applyMode?: StreamingApplyMode;
					hideLoadingScreen?: boolean;
					/** When false, keep the explicit command selection as the AI content range. */
					shouldExpandPositions?: boolean;
				},
			) => string;
			/**
			 * Notify the orchestrator that ADF schema content was rejected during
			 * chunk processing (e.g. blockCard inside listItem). Triggers an
			 * apply_failed_adf_schema_error anomaly and suppresses the success event.
			 */
			notifySchemaContentRejection: (actionId: string, errorMessage?: string) => void;
			/**
			 * Replace the accumulated value for an active streaming action with a full
			 * snapshot. Functionally identical to `streamValue` at the orchestrator-queue
			 * layer today (the queue already resets its internal accumulator before
			 * processing each chunk), but exposed as a separate action so callers that
			 * explicitly want replace semantics (e.g. the HTML+ v2 incremental
			 * tool-call streaming contract) can opt into them by name and so we can
			 * evolve the two paths independently in the future.
			 *
			 * The caller decides when to use this instead of `streamValue` (e.g.
			 * for the HTML+ v2 incremental tool-call streaming contract).
			 *
			 * @param actionId  The actionId returned by a previous `initAction` call.
			 * @param fullValue The complete, up-to-date snapshot for the action. The
			 *                  orchestrator MUST treat this as the new full value rather
			 *                  than as a delta appended to any previous value.
			 */
			replaceValue: (actionId: string, fullValue: string) => void;
			/**
			 * Resume a tool call aborted by the queue timeout, re-applying its content
			 * at the stored (mapped) position instead of re-resolving to the current
			 * selection / end of document.
			 *
			 * Runs within the still-open session via the same commit write as
			 * `commitToolCall`: skips the tool-call action, drives the AI modal at the
			 * stored `startPos` / `aiContentEndPos`, writes the snapshot, and finishes.
			 * Falls back to re-running the original command if no positions were stored
			 * (should not happen — content is written before abort).
			 *
			 * @param toolCallId The id the aborted positions were stored against.
			 * @param command    The original streaming command for the aborted tool call.
			 * @param fullValue  The complete, up-to-date snapshot to re-apply.
			 */
			resumeAction: (toolCallId: string, command: StreamingCommand, fullValue: string) => void;
			startSession: (
				traceId?: string,
				metadata?: {
					agentAttribution?: AgentAttribution;
					agentInfo?: AgentInfo;
					/**
					 * True when the originating edit was from Rovo chat (`chat_edit` or
					 * Create-with-Rovo).
					 */
					isChatEditSession?: boolean;
					/** True for a create stream; suppresses Post Stream Review. */
					isCreateSession?: boolean;
					/**
					 * True for an inline-edit stream; gates the Post Stream
					 * Review auto-open on the orchestrated (Rovo-chat) update path.
					 */
					isInlineEditSession?: boolean;
					promptVersion?: string;
					singleInstrumentationId?: string;
				},
			) => string;
			/**
			 * @param options.isComplete
			 *   Set to `true` when `chunk`'s accumulated value is a fully-closed,
			 *   parseable document (e.g. the streaming JSON parser reports no open
			 *   structures remain). When this is `true`, the orchestrator renders the
			 *   document's last leaf node immediately instead of withholding it for the
			 *   next chunk/finish. Withholding the last leaf exists to avoid rendering a
			 *   node whose content may still be partial (e.g. a link whose href is
			 *   mid-stream); when the caller already knows the document is
			 *   complete, holding it back only delays visible content with no
			 *   correctness benefit.
			 */
			streamValue: (actionId: string, chunk: string, options?: { isComplete?: boolean }) => void;
		};
		dependencies: [
			AIPlugin,
			OptionalPlugin<LocalIdPlugin>,
			OptionalPlugin<AnalyticsPlugin>,
			OptionalPlugin<SelectionPlugin>,
		];
		pluginConfiguration?: AiStreamingOrchestratorPluginOptions;
	}
>;
// #endregion
